import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Avatar from '@material-ui/core/Avatar'
import IconButton from '@material-ui/core/IconButton'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemAvatar from '@material-ui/core/ListItemAvatar'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import ListItemText from '@material-ui/core/ListItemText'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import LinkIcon from '@material-ui/icons/Link'

import { iconFor, tintFor } from './folders'
import { HEADER_HEIGHT } from './layout'

const useStyles = makeStyles(theme => ({
  rail: {
    width: 248,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
    // A faint ground rather than `background.paper`. The rail sits between the
    // charcoal pack rail and the white content pane, and painted the same white
    // as the pane it stopped reading as its own column at all.
    backgroundColor: '#F7F8F7',
    textAlign: 'left',
  },
  // Matches the pack rail and the content pane, so the three rules under the
  // three headers read as one line across the page rather than three steps.
  head: {
    height: HEADER_HEIGHT,
    boxSizing: 'border-box',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(0, 2),
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  },
  scroll: {
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingTop: theme.spacing(0.5),
  },
  item: {
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 12,
    // Room for the copy button, which now keeps the secondary slot for itself.
    paddingRight: 44,
    borderLeft: '2px solid transparent',
    '&:hover $copy': { opacity: 1 },
    '&$selected': {
      borderLeftColor: 'var(--tint)',
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      '& $label': { fontWeight: 500 },
    },
  },
  // Required by the `$selected` reference above; MUI applies this class when
  // `selected` is set, and an empty rule is how JSS gives us the handle.
  selected: {},
  avatarSlot: {
    minWidth: 48,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 6,
    backgroundColor: 'var(--tint)',
    color: theme.palette.common.white,
  },
  avatarIcon: {
    fontSize: '1.25rem',
  },
  label: {
    fontSize: '0.875rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // The file count as words on its own line. A number a teacher can read
  // beats one they have to decode, and moving it here means it no longer has
  // to trade places with the copy button on hover - which made it vanish just
  // as you reached for it, and put it out of reach entirely on a touch screen.
  meta: {
    fontSize: '0.75rem',
  },
  copy: {
    padding: 6,
    // Visible on hover and whenever it holds focus, so the row stays quiet at
    // rest without the control becoming keyboard-only folklore.
    opacity: 0,
    transition: 'opacity 120ms',
    '&:focus': { opacity: 1 },
  },
  copyIcon: { fontSize: '1.05rem' },
}))

/** Files in a folder, said the way a teacher would say it. */
function metaFor(node) {
  return `${node.count} ${node.count === 1 ? 'file' : 'files'}`
}

/**
 * The folder picker.
 *
 * One row per folder, flat, with nothing to expand. That is a consequence of
 * the depth cap (D33) rather than a simplification of it: with nesting capped
 * at three levels, levels 2 and 3 both fit on the page as sections and groups,
 * so there is nothing left below a folder for the rail to navigate to.
 *
 * The tree this replaced had to grow and shrink as you moved through it, which
 * put the page's structure in the one column least able to show it. A fixed
 * five rows is the whole point: the rail answers "which kind of thing" and then
 * stops asking for attention.
 *
 * Rows borrow the pack rail's own grammar - an avatar, a name, a line of
 * detail - so the two columns read as one family rather than two unrelated
 * lists. Both the tint and the icon are derived from the slug (see
 * `folders.js`), so a folder Kathy adds tomorrow draws itself.
 */
function FolderRail({ roots, activeSlug, onSelectFolder, onCopyLink }) {
  const classes = useStyles()
  const generated = roots.filter(node => node.generated)
  const published = roots.filter(node => !node.generated)

  const renderRow = (node) => {
    const tint = tintFor(node.slug)
    const Icon = iconFor(node.slug, node.generated)
    return (
      <ListItem
        key={node.key}
        button
        selected={node.slug === activeSlug}
        classes={{ root: classes.item, selected: classes.selected }}
        style={{ '--tint': tint }}
        onClick={() => onSelectFolder(node)}
        // MUI's `selected` only paints a class. Screen readers need the
        // state said out loud, so the attribute is set alongside it.
        aria-current={node.slug === activeSlug}
      >
        <ListItemAvatar className={classes.avatarSlot}>
          <Avatar className={classes.avatar} variant='rounded' style={{ '--tint': tint }}>
            <Icon className={classes.avatarIcon} />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={node.name}
          secondary={metaFor(node)}
          primaryTypographyProps={{ className: classes.label }}
          secondaryTypographyProps={{ className: classes.meta }}
        />
        <ListItemSecondaryAction>
          <Tooltip title='Copy link to this folder'>
            <IconButton
              className={classes.copy}
              size='small'
              aria-label={`Copy link to ${node.name}`}
              onClick={() => onCopyLink(node)}
            >
              <LinkIcon className={classes.copyIcon} />
            </IconButton>
          </Tooltip>
        </ListItemSecondaryAction>
      </ListItem>
    )
  }

  return (
    <nav className={classes.rail} aria-label='Folders'>
      <div className={classes.head}>
        <Typography variant='subtitle1'>{'Folders'}</Typography>
      </div>
      <div className={classes.scroll}>
        <List disablePadding>
          {generated.map(renderRow)}
          {published.map(renderRow)}
        </List>
      </div>
    </nav>
  )
}

export { FolderRail }
